import { API_ORIGIN } from '@/lib/api-origin';
import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, AtSign, User, Lock, Loader2, AlertCircle, Check } from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { useAuth } from '@/lib/auth/AuthContext';
import { api, isApiError } from '@/lib/api';
import { signUpSchema, type SignUpValues } from '@/lib/validation/auth.schema';
import { AuthLayout } from '../components/AuthLayout';
import { FormField } from '../components/FormField';
import { OAuthButtons } from '../components/OAuthButtons';
import { GoogleIcon, DiscordIcon } from '../components/BrandIcons';
import { signUpTranslations } from './translations';
import { buildOAuthButton, isManualAuthDisabled, useAuthProviders } from '../hooks/useAuthProviders';

function scorePassword(pw: string): 0 | 1 | 2 | 3 | 4 {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw) && /[^a-zA-Z0-9]/.test(pw)) score++;
  return Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;
}

const STRENGTH_COLORS = ['bg-surface-border', 'bg-brand-danger', 'bg-amber-500', 'bg-brand-success', 'bg-accent'];
const STRENGTH_KEYS = ['weak', 'fair', 'good', 'strong'] as const;

export function SignUp() {
  const t = useSectionTranslation(signUpTranslations);
  const { user, loading, refresh } = useAuth();
  const oauth = useAuthProviders();
  const manualAuthDisabled = isManualAuthDisabled(oauth.providers);
  const showManualForm = !oauth.loading && !manualAuthDisabled;
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { acceptTerms: false },
  });

  const password = watch('password') ?? '';
  const strength = useMemo(() => scorePassword(password), [password]);
  const strengthKey = strength === 0 ? null : STRENGTH_KEYS[strength - 1];

  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setSubmitting(true);
    const res = await api.post<{ user: unknown }>('/auth/register', values);
    setSubmitting(false);
    if (isApiError(res)) {
      switch (res.error.code) {
        case 'AUTH_EMAIL_TAKEN':
          setServerError(t.errors.emailTaken);
          break;
        case 'AUTH_USERNAME_TAKEN':
          setServerError(t.errors.usernameTaken);
          break;
        case 'AUTH_USERNAME_RESERVED':
          setServerError(t.errors.usernameReserved);
          break;
        case 'NETWORK_ERROR':
          setServerError(t.errors.network);
          break;
        default:
          setServerError(res.error.message || t.errors.generic);
      }
      return;
    }
    await refresh();
    navigate('/');
  });

  return (
    <AuthLayout title={t.page}>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-display-md text-brand-white mb-2">
            {manualAuthDisabled ? t.oauthOnly.heading : t.heading}
          </h1>
          <p className="text-body-sm text-brand-muted">
            {manualAuthDisabled ? t.oauthOnly.subtext : t.subtext}
          </p>
        </div>

        {serverError && (
          <div
            className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger"
            role="alert"
          >
            <AlertCircle size={16} />
            {serverError}
          </div>
        )}

        {oauth.loading && (
          <div className="flex justify-center py-6" aria-busy="true">
            <Loader2 size={20} className="animate-spin text-brand-muted" />
          </div>
        )}

        {showManualForm && (
        <>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <FormField
            id="email"
            label={t.form.email}
            type="email"
            placeholder={t.form.emailPlaceholder}
            autoComplete="email"
            required
            icon={<Mail size={16} />}
            error={errors.email?.message}
            registration={register('email')}
          />

          <FormField
            id="username"
            label={t.form.username}
            placeholder={t.form.usernamePlaceholder}
            autoComplete="username"
            required
            icon={<AtSign size={16} />}
            error={errors.username?.message}
            registration={register('username')}
          />

          <FormField
            id="displayName"
            label={t.form.displayName}
            placeholder={t.form.displayNamePlaceholder}
            autoComplete="nickname"
            required
            icon={<User size={16} />}
            error={errors.displayName?.message}
            registration={register('displayName')}
          />

          <div>
            <FormField
              id="password"
              label={t.form.password}
              type="password"
              placeholder={t.form.passwordPlaceholder}
              autoComplete="new-password"
              required
              icon={<Lock size={16} />}
              error={errors.password?.message}
              registration={register('password')}
            />
            {strength > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex flex-1 gap-1">
                  {[1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i <= strength ? STRENGTH_COLORS[strength] : 'bg-surface-border'
                      }`}
                    />
                  ))}
                </div>
                {strengthKey && (
                  <span className="text-[11px] text-brand-muted min-w-[3rem] text-end">
                    {t.strength[strengthKey]}
                  </span>
                )}
              </div>
            )}
          </div>

          <FormField
            id="confirmPassword"
            label={t.form.confirmPassword}
            type="password"
            placeholder={t.form.confirmPasswordPlaceholder}
            autoComplete="new-password"
            required
            icon={<Lock size={16} />}
            error={errors.confirmPassword?.message}
            registration={register('confirmPassword')}
          />

          <label className="flex items-start gap-2.5 text-sm text-brand-muted cursor-pointer">
            <input
              type="checkbox"
              {...register('acceptTerms')}
              className="mt-0.5 h-4 w-4 rounded border-surface-border bg-surface-card text-accent focus:ring-accent/40"
            />
            <span>{t.form.acceptTerms}</span>
          </label>
          {errors.acceptTerms?.message && (
            <p className="flex items-center gap-1 text-xs text-brand-danger -mt-2" role="alert">
              <AlertCircle size={12} />
              {errors.acceptTerms.message}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary h-11 w-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {t.form.submitting}
              </>
            ) : (
              <>
                <Check size={16} />
                {t.form.submit}
              </>
            )}
          </button>
        </form>

        <div className="relative py-2">
          <div className="absolute inset-x-0 top-1/2 h-px bg-surface-border" />
          <span className="relative bg-surface-base px-3 text-xs text-brand-muted inline-block start-1/2 -translate-x-1/2">
            {t.divider}
          </span>
        </div>
        </>
        )}

        <OAuthButtons
          providers={[
            buildOAuthButton(oauth.providers, { id: 'google', label: t.oauth.google, href: `${API_ORIGIN}/api/auth/google`, Icon: GoogleIcon }, t.oauth.unavailable),
            buildOAuthButton(oauth.providers, { id: 'discord', label: t.oauth.discord, href: `${API_ORIGIN}/api/auth/discord`, Icon: DiscordIcon, iconColor: 'text-[#5865F2]' }, t.oauth.unavailable),
          ]}
        />

        <p className="text-center text-sm text-brand-muted">
          {t.signin.lead}{' '}
          <Link to="/auth/signin" className="text-accent hover:underline font-medium">
            {t.signin.link}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}